import { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  Divider,
  IconButton,
  InputLabel,
  Link,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import WebOutlinedIcon from "@mui/icons-material/WebOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import CustomInput from "global/components/CustomInput/CustomInput";
import customInputStyles from "global/components/CustomInput/CustomInput.styles";
import formFieldMetrics from "global/components/CustomInput/FormField.tokens";
import { openErrorNotification, openSuccessNotification } from "helpers/methods";
import { apiKeys as styles } from "./ApiKeys.styles";
import {
  EmbedOrigin,
  addEmbedOrigin,
  fetchEmbedOrigins,
  removeEmbedOrigin,
} from "./EmbedOriginsService";

const ORIGIN_FIELD_ID = "embed_origin";

/**
 * Settings > Integrations: the web origins allowed to frame this account's embedded signing and sending
 * widgets. A token can only be minted for a registered origin, and it is only redeemed when the page
 * showing the widget is that origin and it is still registered. Account admins only -- others see why they cannot edit.
 */
const EmbedOrigins = ({ announce }: { announce: (message: string, priority?: any) => void }) => {
  const theme = useTheme();
  const [origins, setOrigins] = useState<EmbedOrigin[]>([]);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [origin, setOrigin] = useState("");
  const [originError, setOriginError] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setOrigins(await fetchEmbedOrigins());
    } catch (error: any) {
      if (error?.statusCode === 403) {
        setForbidden(true);
      } else {
        openErrorNotification(error?.message ?? "Couldn't load embed origins.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleAdd = async () => {
    const value = origin.trim();
    if (!value) {
      setOriginError("Enter an origin such as https://app.example.com");
      return;
    }
    try {
      setSaving(true);
      await addEmbedOrigin(value);
      setOrigin("");
      openSuccessNotification("Origin added.");
      announce("Origin added.");
      await load();
    } catch (error: any) {
      setOriginError(error?.message ?? "Couldn't add this origin.");
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (row: EmbedOrigin) => {
    try {
      await removeEmbedOrigin(row.id);
      openSuccessNotification("Origin removed.");
      announce("Origin removed.");
      await load();
    } catch (error: any) {
      openErrorNotification(error?.message ?? "Couldn't remove this origin.");
    }
  };

  return (
    <Card variant="outlined" sx={{ ...styles.cardSx, mt: 3 }}>
      <Box sx={styles.sectionHeaderSx}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box sx={styles.sectionGlyphSx}>
            <WebOutlinedIcon sx={{ color: "primary.main", fontSize: 15 }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography component="h2" sx={styles.sectionTitleSx}>
              Embedded signing origins
            </Typography>
            <Typography component="span" sx={styles.sectionDescSx}>
              Websites allowed to show Inkfree signing and sending inside an
              iframe. Use the exact origin, for example https://app.example.com.{" "}
              {/* Underlined: it sits in grey body text, and color alone
                  would not mark it as a link (WCAG 1.4.1). */}
              <Link
                href="https://inkfree.app/docs/embed/"
                target="_blank"
                rel="noopener"
                underline="always"
              >
                Partner guide
              </Link>
            </Typography>
          </Box>
        </Stack>
      </Box>

      <Divider sx={{ borderColor: theme.palette.neutral[200] }} />

      <Box sx={{ p: 2.5 }} aria-busy={loading}>
        {forbidden ? (
          <Typography variant="body2">
            Only account admins can manage embed origins.
          </Typography>
        ) : (
          <>
            {/* The label sits above the row, not inside the input, so the input and the button share one
                top edge and both are the field height; an error under the input no longer moves the button. */}
            <Box mb={formFieldMetrics.labelGap}>
              <InputLabel htmlFor={ORIGIN_FIELD_ID} sx={customInputStyles.inputLabel}>
                Origin
              </InputLabel>
            </Box>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.5}
              alignItems={{ xs: "stretch", sm: "flex-start" }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <CustomInput
                  id={ORIGIN_FIELD_ID}
                  placeHolder="https://app.example.com"
                  name="embedOrigin"
                  type="text"
                  value={origin}
                  onChange={(event: any) => {
                    setOrigin(event.target.value);
                    if (originError) setOriginError("");
                  }}
                  error={originError}
                />
              </Box>
              <Button
                variant="contained"
                startIcon={<AddOutlinedIcon />}
                onClick={handleAdd}
                disabled={saving || loading}
                sx={{
                  ...styles.createButtonSx,
                  width: { xs: "100%", sm: "auto" },
                  height: `${formFieldMetrics.height}px`,
                }}
              >
                {saving ? "Adding…" : "Add origin"}
              </Button>
            </Stack>

            <Box sx={{ mt: 2 }}>
              {!loading && origins.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  No origins yet. Widgets cannot be embedded until one is added.
                </Typography>
              )}
              {origins.map((row) => (
                <Stack
                  key={row.id}
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ py: 1, borderBottom: `1px solid ${theme.palette.neutral[200]}` }}
                >
                  <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
                    {row.origin}
                  </Typography>
                  <Tooltip title="Remove origin">
                    <IconButton
                      aria-label={`Remove ${row.origin}`}
                      onClick={() => handleRemove(row)}
                      size="small"
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              ))}
            </Box>
          </>
        )}
      </Box>
    </Card>
  );
};

export default EmbedOrigins;
